import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { Actions, ofType, createEffect, ROOT_EFFECTS_INIT } from '@ngrx/effects'
import { of, forkJoin } from 'rxjs'
import { switchMap, map, catchError, tap, take, mapTo, distinctUntilChanged } from 'rxjs/operators'

import { AppState, getReleaseInfo } from '..'
import * as ReleaseInfoActions from '../actions/release-info.actions'
import * as UserActions from '../actions/user.actions'
import { AuthService, SentryContextService } from '../../common'
import { JrpcService } from '../../common/jrpc.service'

const intervalTimeout: number = 60 * 1000 // one minute in MS

@Injectable()
export class ReleaseInfoEffects {
  init$ = createEffect(() => this.actions$.pipe(ofType(ROOT_EFFECTS_INIT), mapTo(ReleaseInfoActions.loadReleaseInfo())))

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.authenticateSuccess, ReleaseInfoActions.loadReleaseInfo),
      switchMap(() =>
        forkJoin([this.authService.getBackendVersionInfo(), this.authService.getFrontendVersionInfo()]).pipe(
          map(([backend, frontend]) => {
            const versions = {
              ...backend,
              frontend_version: frontend,
            }
            this.sentryContextService.setReleaseVersions(versions)
            return ReleaseInfoActions.loadReleaseInfoSuccess({ data: versions })
          }),
          catchError((error) => of(ReleaseInfoActions.loadReleaseInfoFailure({ error }))),
        ),
      ),
    ),
  )

  refreshFrontendVersion$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReleaseInfoActions.refreshReleaseInfo),
        switchMap(() =>
          forkJoin([
            this.store.select(getReleaseInfo).pipe(take(1)),
            this.authService.getFrontendVersionInfo(),
            this.authService.getBackendVersionInfo(),
          ]),
        ),
        tap(([currentReleaseInfo, newFrontendVersion, newBackendInfo]) => {
          if (
            currentReleaseInfo.frontend_version !== newFrontendVersion ||
            currentReleaseInfo.backend_version !== newBackendInfo.backend_version ||
            currentReleaseInfo.release_version !== newBackendInfo.release_version
          ) {
            location.reload()
          }
        }),
      ),
    {
      dispatch: false,
    },
  )

  private refreshInfoInterval: number = null

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private store: Store<AppState>,
    private jrpc: JrpcService,
    private sentryContextService: SentryContextService,
  ) {
    this.jrpc.wsOpened$.pipe(distinctUntilChanged()).subscribe((opened) => {
      if (opened) {
        this.refreshInfoInterval = window.setInterval(() => {
          this.store.dispatch(ReleaseInfoActions.refreshReleaseInfo())
        }, intervalTimeout)
      } else {
        window.clearInterval(this.refreshInfoInterval)
      }
    })
  }
}
